import type { MeetOutcome } from "../competition/vocab";
import { boutProfileFor } from "../competition/bout-profile";
import { boutWinner } from "../competition/ranking";
import type { BoutResultDetail, SaveResultsBody } from "../competition/results";
import { tsrCamelKeys } from "./sets-live";
import type { TsrSaveSlot } from "./sets-mapping";

/**
 * TSR BOXING on the wire. Two operators, two writers, so two keys and revs
 * (two writers on one key clobber):
 *
 *   tsr_boxing_state  TsrBoxingEnvelope      the bout controller: clock, judges' cards, decision
 *   tsr_boxing_calls  TsrBoxingCallsEnvelope the statistics station: the referee's calls and
 *                                            the punches landed (a tally per corner, round, type)
 *   join_boxing { meetId } → ack { success, state, calls, degraded }   room boxing_<meetId>
 *   leave_boxing { meetId }
 *   join_boxing_feed { meetId } → ack { success, meetId, feed, degraded }   room boxing_feed_<meetId>
 *   leave_boxing_feed { meetId }
 *   boxing_feed_updated  TsrBoxingFeed (the stable public shape, camelCase like the other feeds)
 *   GET /api/tsr/boxing/live | /:meetId/state | /:meetId/scoreboard (public, read-only)
 *
 * Redis only, 24 h. Save Meet is the operator's `PATCH /meets/:id/results`
 * (BOUT) through `tsrBoxingSaveBody`; nothing here writes Postgres.
 *
 * Red is side A, blue side B, whatever the slots' start order. Rules are
 * `boutProfileFor("BOX")`: 3 × 3:00, five judges, the 10-point must system,
 * decided by the judges' majority. The feed carries what the referee and the
 * judges do, and the punches landed by type as the statistics station counts
 * them (user, 2026-11-03) — a statistic only: Save Meet does not write it.
 */

export const TSR_BOXING_SPORT_CODE = "BOX";
export const TSR_BOXING_STATE_TTL_SECONDS = 86_400;
/** World Boxing's minute between rounds. */
export const TSR_BOXING_REST_SECONDS = 60;
/** Most calls a bout's log may hold: a bound on the packet, far above any real bout. */
export const TSR_BOXING_CALLS_MAX = 120;

export const TSR_BOXING_PHASES = ["pre", "round", "rest", "decision", "final"] as const;
export type TsrBoxingPhase = (typeof TSR_BOXING_PHASES)[number];

export type TsrBoxingCorner = "R" | "B";

/** One judge's one round: [red, blue]. */
export type TsrBoxingRoundCard = [number, number];

export const TSR_BOXING_CALL_KINDS = ["caution", "warning", "count"] as const;
export type TsrBoxingCallKind = (typeof TSR_BOXING_CALL_KINDS)[number];

/** The punches the statistics station counts when they land. */
export const TSR_BOXING_PUNCH_TYPES = ["jab", "cross", "hook", "uppercut"] as const;
export type TsrBoxingPunchType = (typeof TSR_BOXING_PUNCH_TYPES)[number];
/** Most tally rows a bout may hold (2 corners × rounds × types): a bound on the packet. */
export const TSR_BOXING_PUNCH_ROWS_MAX = 240;
/** One count can never pass this: a typo guard, far above any real round. */
export const TSR_BOXING_PUNCH_COUNT_MAX = 999;

/** The clock as a stamp, so a board runs it between packets. `stampedAt` null = stopped. */
export interface TsrBoxingClock {
	remainingMs: number;
	stampedAt: string | null;
}

export interface TsrBoxingDecision {
	winner: TsrBoxingCorner;
	/** `boutProfileFor("BOX").winMethods`: WP RSC RSCI KO ABD DSQ WO. */
	method: string;
	/** The round a stoppage ended in. */
	round: number | null;
}

export interface TsrBoxingState {
	rounds: number;
	roundSeconds: number;
	restSeconds: number;
	judges: number;
	phase: TsrBoxingPhase;
	/** 1-based: the round in progress, or the one just ended. */
	round: number;
	clock: TsrBoxingClock;
	/** `cards[judge][round]`, null until that judge's card for the round is in. */
	cards: (TsrBoxingRoundCard | null)[][];
	decision: TsrBoxingDecision | null;
}

export interface TsrBoxingCall {
	id: string;
	round: number;
	/** Seconds into the round. */
	atSeconds: number;
	kind: TsrBoxingCallKind;
	corner: TsrBoxingCorner;
	reason: string;
}

/**
 * Punches landed by one corner in one round, of one type. A TALLY, not a log:
 * a bout lands hundreds, so each tap moves a count. Rows at 0 are dropped.
 */
export interface TsrBoxingPunchTally {
	corner: TsrBoxingCorner;
	round: number;
	type: TsrBoxingPunchType;
	landed: number;
}

export interface TsrBoxingCallsState {
	calls: TsrBoxingCall[];
	/** Absent from a station older than the punch counter: no punches. */
	punches?: TsrBoxingPunchTally[];
}

/** Punches landed, by type and in all, for a corner — every round, or one. */
export type TsrBoxingPunchCounts = Record<TsrBoxingPunchType, number> & { total: number };

export function tsrBoxingPunchCounts(calls: Pick<TsrBoxingCallsState, "punches"> | null | undefined, corner: TsrBoxingCorner, round?: number): TsrBoxingPunchCounts {
	const out: TsrBoxingPunchCounts = { jab: 0, cross: 0, hook: 0, uppercut: 0, total: 0 };
	for (const p of calls?.punches ?? []) {
		if (p.corner !== corner || (round !== undefined && p.round !== round)) continue;
		out[p.type] += p.landed;
		out.total += p.landed;
	}
	return out;
}

export interface TsrBoxingEnvelope {
	meetId: string;
	rev: number;
	state: TsrBoxingState;
}
export interface TsrBoxingCallsEnvelope {
	meetId: string;
	rev: number;
	state: TsrBoxingCallsState;
}
export interface TsrStoredBoxingState extends TsrBoxingEnvelope {
	updatedAt: string;
}
export interface TsrStoredBoxingCalls extends TsrBoxingCallsEnvelope {
	updatedAt: string;
}

/**
 * Red and blue of a pair ordered A / B. A slot's start order says nothing
 * about its corner, so a slot that names BLUE first, or RED second, swaps them.
 */
export function tsrBoxingCorners<T extends { side?: string | null }>(a: T, b: T): { red: T; blue: T; swapped: boolean } {
	const swapped = a.side?.toUpperCase() === "BLUE" || b.side?.toUpperCase() === "RED";
	return swapped ? { red: b, blue: a, swapped } : { red: a, blue: b, swapped };
}

// ─── Reading a state ─────────────────────────────────────────────────────────

export function tsrBoxingInitialState(input: { rounds?: number | undefined; roundSeconds?: number | undefined; judges?: number | undefined } = {}): TsrBoxingState {
	const p = boutProfileFor(TSR_BOXING_SPORT_CODE);
	const rounds = input.rounds ?? p.defaultRounds ?? 3;
	const roundSeconds = input.roundSeconds ?? p.defaultRoundSeconds ?? 180;
	const judges = input.judges ?? p.judges;
	return {
		rounds,
		roundSeconds,
		restSeconds: TSR_BOXING_REST_SECONDS,
		judges,
		phase: "pre",
		round: 1,
		clock: { remainingMs: roundSeconds * 1000, stampedAt: null },
		cards: Array.from({ length: judges }, () => Array.from({ length: rounds }, () => null)),
		decision: null,
	};
}

const isScore = (v: unknown): boolean => typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 10;
const isCard = (v: unknown): boolean => v === null || (Array.isArray(v) && v.length === 2 && v.every(isScore));
const isCorner = (v: unknown): v is TsrBoxingCorner => v === "R" || v === "B";

/** Structural check of what a controller sent: enough for every reader, no more. */
export function tsrIsBoxingState(v: unknown): v is TsrBoxingState {
	const s = v as Partial<TsrBoxingState> | null;
	if (!s || typeof s !== "object") return false;
	if (![s.rounds, s.roundSeconds, s.restSeconds, s.judges, s.round].every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0)) return false;
	if (!TSR_BOXING_PHASES.includes(s.phase as TsrBoxingPhase)) return false;
	if (!s.clock || typeof s.clock.remainingMs !== "number") return false;
	if (!Array.isArray(s.cards) || s.cards.length !== s.judges) return false;
	if (!s.cards.every((row) => Array.isArray(row) && row.length === s.rounds && row.every(isCard))) return false;
	return s.decision === null || (!!s.decision && isCorner(s.decision.winner) && typeof s.decision.method === "string");
}

export function tsrIsBoxingCalls(v: unknown): v is TsrBoxingCallsState {
	const s = v as Partial<TsrBoxingCallsState> | null;
	if (!s || !Array.isArray(s.calls) || s.calls.length > TSR_BOXING_CALLS_MAX) return false;
	const callsOk = s.calls.every((c) => !!c && typeof c.id === "string" && Number.isInteger(c.round) && typeof c.atSeconds === "number" && TSR_BOXING_CALL_KINDS.includes(c.kind) && isCorner(c.corner) && typeof c.reason === "string");
	if (!callsOk) return false;
	if (s.punches === undefined) return true;
	return (
		Array.isArray(s.punches) &&
		s.punches.length <= TSR_BOXING_PUNCH_ROWS_MAX &&
		s.punches.every(
			(p) => !!p && isCorner(p.corner) && Number.isInteger(p.round) && p.round >= 1 && TSR_BOXING_PUNCH_TYPES.includes(p.type) && Number.isInteger(p.landed) && p.landed >= 0 && p.landed <= TSR_BOXING_PUNCH_COUNT_MAX,
		)
	);
}

// ─── Reading it over REST (OVR) ──────────────────────────────────────────────

/**
 * `GET /tsr/boxing/:id/state` → `data.state` as the bout controller's state,
 * or null when it is not one. The REST envelope snake-cases every key
 * (`round_seconds`, `clock.remaining_ms`); values keep their case ("R", "WP").
 */
export function tsrBoxingStateFromWire(wire: unknown): TsrBoxingState | null {
	if (!wire || typeof wire !== "object" || Array.isArray(wire)) return null;
	const s = tsrCamelKeys(wire);
	return tsrIsBoxingState(s) ? s : null;
}

/** `data.calls` → the statistics station's log; anything unreadable reads as no calls. */
export function tsrBoxingCallsFromWire(wire: unknown): TsrBoxingCallsState {
	const s = tsrCamelKeys(wire);
	return tsrIsBoxingCalls(s) ? s : { calls: [] };
}

/**
 * What a save of this bout would write, as one string: the cards of the
 * rounds every judge has scored, the decision, and the calls the save counts
 * (warnings per round, standing counts, cautions per corner). It moves when
 * a round's last card lands, a decision is given or a call is logged or
 * undone — never on a part-keyed round or the clock. null = nothing to save
 * yet (no complete round, no decision).
 */
export function tsrBoxingMilestone(state: TsrBoxingState, calls: TsrBoxingCallsState | null): string | null {
	const reached = tsrBoxingRoundsScored(state);
	if (reached === 0 && !state.decision) return null;
	const cards = state.cards.map((row) => row.slice(0, reached).map((c) => `${c![0]}-${c![1]}`).join(",")).join("/");
	const d = state.decision;
	const decision = d ? `${d.winner}${d.method}${d.round ?? ""}` : "";
	const counted = (calls?.calls ?? []).map((c) => `${c.kind}${c.corner}${c.kind === "warning" ? c.round : ""}`).sort().join(",");
	return `${decision}|${cards}|${counted}`;
}

/**
 * The 10-point must system: the round's winner on a card gets 10, the other 9
 * or fewer, with no level round; a warning takes a point off afterwards, so a
 * side may also finish a round below 10 with no 10 on the card. CONFIRM the
 * floor (6) with the technical delegate.
 */
export function tsrBoxingCardProblem(card: TsrBoxingRoundCard, warned: { R: boolean; B: boolean } = { R: false, B: false }): "RANGE" | "NO_TEN" | "LEVEL" | null {
	const [r, b] = card;
	if (r < 6 || b < 6 || r > 10 || b > 10) return "RANGE";
	if (r === b && !warned.R && !warned.B) return "LEVEL";
	if (r !== 10 && b !== 10 && !warned.R && !warned.B) return "NO_TEN";
	return null;
}

/** The running clock at `now`, whole-second-ready milliseconds, never below 0. */
export function tsrBoxingRemainingMs(clock: TsrBoxingClock, now: number): number {
	if (!clock.stampedAt) return Math.max(0, clock.remainingMs);
	return Math.max(0, clock.remainingMs - (now - Date.parse(clock.stampedAt)));
}

/** Rounds every judge has scored, counted from round 1. A gap stops the count: boards never show part of a round. */
export function tsrBoxingRoundsScored(s: Pick<TsrBoxingState, "cards" | "rounds">): number {
	let n = 0;
	while (n < s.rounds && s.cards.length > 0 && s.cards.every((row) => row[n])) n++;
	return n;
}

/** Each judge's [red, blue] total over the first `upto` rounds. */
export function tsrBoxingTotals(s: Pick<TsrBoxingState, "cards">, upto: number): [number, number][] {
	return s.cards.map((row) => row.slice(0, upto).reduce<[number, number]>((a, c) => (c ? [a[0] + c[0], a[1] + c[1]] : a), [0, 0]));
}

/** Who leads a card, "" when level. */
export const tsrBoxingLeader = ([r, b]: readonly [number, number]): TsrBoxingCorner | "" => (r > b ? "R" : b > r ? "B" : "");

export interface TsrBoxingVotes {
	red: number;
	blue: number;
}

/** Judges ahead over the first `upto` rounds. */
export function tsrBoxingVotes(s: Pick<TsrBoxingState, "cards">, upto: number): TsrBoxingVotes {
	const leaders = tsrBoxingTotals(s, upto).map(tsrBoxingLeader);
	return { red: leaders.filter((l) => l === "R").length, blue: leaders.filter((l) => l === "B").length };
}

/** One round across the cards. Null until every judge has scored it. */
export function tsrBoxingRoundResult(s: Pick<TsrBoxingState, "cards">, round: number): { winner: TsrBoxingCorner | ""; votes: TsrBoxingVotes } | null {
	const cards = s.cards.map((row) => row[round - 1] ?? null);
	if (cards.length === 0 || cards.some((c) => !c)) return null;
	const leaders = (cards as TsrBoxingRoundCard[]).map(tsrBoxingLeader);
	const votes = { red: leaders.filter((l) => l === "R").length, blue: leaders.filter((l) => l === "B").length };
	return { winner: votes.red > votes.blue ? "R" : votes.blue > votes.red ? "B" : "", votes };
}

/** A round's cards as they stand, for a board that follows the table live: complete or not. */
export function tsrBoxingRoundTally(s: Pick<TsrBoxingState, "cards">, round: number): { votes: TsrBoxingVotes; entered: number; leader: TsrBoxingCorner | "" } {
	const cards = s.cards.flatMap((row) => (row[round - 1] ? [row[round - 1] as TsrBoxingRoundCard] : []));
	const leaders = cards.map(tsrBoxingLeader);
	const votes = { red: leaders.filter((l) => l === "R").length, blue: leaders.filter((l) => l === "B").length };
	return { votes, entered: cards.length, leader: votes.red > votes.blue ? "R" : votes.blue > votes.red ? "B" : "" };
}

/** "Unanimous decision" 5–0, "Majority decision" with a level card, "Split decision" otherwise. */
export function tsrBoxingDecisionType(votes: TsrBoxingVotes, judges: number): "UNANIMOUS" | "MAJORITY" | "SPLIT" {
	const lo = Math.min(votes.red, votes.blue);
	if (lo > 0) return "SPLIT";
	return votes.red + votes.blue === judges ? "UNANIMOUS" : "MAJORITY";
}

/** Who the cards name when all are in, for the controller to offer. Null: not all in, or level. */
export function tsrBoxingCardsWinner(s: Pick<TsrBoxingState, "cards" | "rounds">): TsrBoxingCorner | null {
	if (tsrBoxingRoundsScored(s) < s.rounds) return null;
	const v = tsrBoxingVotes(s, s.rounds);
	return v.red > v.blue ? "R" : v.blue > v.red ? "B" : null;
}

// ─── The public feed ─────────────────────────────────────────────────────────

export type TsrBoxingFeedStatus = "upcoming" | "live" | "break" | "decision" | "final";

/** The stable feed for third parties. Additive only: never rename or remove a field. */
export interface TsrBoxingFeed {
	meetId: string;
	rev: number;
	callsRev: number;
	status: TsrBoxingFeedStatus;
	updatedAt: string;
	format: { rounds: number; roundSeconds: number; restSeconds: number; judges: number };
	round: number;
	clock: { running: boolean; remainingMs: number; stampedAt: string | null };
	/** Rounds every judge has scored: `roundResults` holds these. */
	roundsScored: number;
	roundResults: { round: number; winner: "red" | "blue" | null; votes: TsrBoxingVotes; cards: { judge: string; red: number; blue: number }[] }[];
	/** Running totals, live: every card keyed so far, a part-entered round included. */
	judges: { judge: string; red: number; blue: number; leader: "red" | "blue" | null }[];
	/** Each judge's cards as they are keyed. */
	cards: { judge: string; rounds: { round: number; red: number; blue: number }[] }[];
	judgesAhead: TsrBoxingVotes;
	calls: { id: string; round: number; atSeconds: number; kind: TsrBoxingCallKind; corner: "red" | "blue"; reason: string }[];
	counts: { red: { cautions: number; warnings: number; standingCounts: number }; blue: { cautions: number; warnings: number; standingCounts: number } };
	/** Punches landed by type, as the statistics station counts them: the bout, and round by round (rounds with any). */
	punches: { red: TsrBoxingPunchCounts; blue: TsrBoxingPunchCounts; rounds: { round: number; red: TsrBoxingPunchCounts; blue: TsrBoxingPunchCounts }[] };
	decision: { winner: "red" | "blue"; method: string; type: "UNANIMOUS" | "MAJORITY" | "SPLIT" | null; round: number | null; votes: TsrBoxingVotes } | null;
}

const name = (c: TsrBoxingCorner | ""): "red" | "blue" | null => (c === "R" ? "red" : c === "B" ? "blue" : null);
const seat = (j: number) => `J${j + 1}`;

export function tsrBoxingFeedStatus(phase: TsrBoxingPhase): TsrBoxingFeedStatus {
	return phase === "pre" ? "upcoming" : phase === "round" ? "live" : phase === "rest" ? "break" : phase;
}

export function tsrBoxingFeed(stored: TsrStoredBoxingState, calls: Pick<TsrStoredBoxingCalls, "rev" | "state"> | null): TsrBoxingFeed {
	const s = stored.state;
	const scored = tsrBoxingRoundsScored(s);
	const log = calls?.state.calls ?? [];
	const count = (corner: TsrBoxingCorner) => {
		const of = (kind: TsrBoxingCallKind) => log.filter((c) => c.corner === corner && c.kind === kind).length;
		return { cautions: of("caution"), warnings: of("warning"), standingCounts: of("count") };
	};
	const votes = tsrBoxingVotes(s, s.rounds);
	const onCards = s.decision?.method === "WP";
	return {
		meetId: stored.meetId,
		rev: stored.rev,
		callsRev: calls?.rev ?? 0,
		status: tsrBoxingFeedStatus(s.phase),
		updatedAt: stored.updatedAt,
		format: { rounds: s.rounds, roundSeconds: s.roundSeconds, restSeconds: s.restSeconds, judges: s.judges },
		round: s.round,
		clock: { running: !!s.clock.stampedAt, remainingMs: s.clock.remainingMs, stampedAt: s.clock.stampedAt },
		roundsScored: scored,
		roundResults: Array.from({ length: scored }, (_, i) => {
			const r = tsrBoxingRoundResult(s, i + 1)!;
			return {
				round: i + 1,
				winner: name(r.winner),
				votes: r.votes,
				cards: s.cards.map((row, j) => {
					const c = row[i]!;
					return { judge: seat(j), red: c[0], blue: c[1] };
				}),
			};
		}),
		judges: tsrBoxingTotals(s, s.rounds).map((t, j) => ({ judge: seat(j), red: t[0], blue: t[1], leader: name(tsrBoxingLeader(t)) })),
		cards: s.cards.map((row, j) => ({ judge: seat(j), rounds: row.flatMap((c, r) => (c ? [{ round: r + 1, red: c[0], blue: c[1] }] : [])) })),
		judgesAhead: votes,
		calls: log.map((c) => ({ id: c.id, round: c.round, atSeconds: c.atSeconds, kind: c.kind, corner: name(c.corner)!, reason: c.reason })),
		counts: { red: count("R"), blue: count("B") },
		punches: {
			red: tsrBoxingPunchCounts(calls?.state, "R"),
			blue: tsrBoxingPunchCounts(calls?.state, "B"),
			rounds: [...new Set((calls?.state.punches ?? []).map((p) => p.round))]
				.sort((a, b) => a - b)
				.map((round) => ({ round, red: tsrBoxingPunchCounts(calls?.state, "R", round), blue: tsrBoxingPunchCounts(calls?.state, "B", round) })),
		},
		decision: s.decision
			? { winner: name(s.decision.winner)!, method: s.decision.method, type: onCards ? tsrBoxingDecisionType(votes, s.judges) : null, round: s.decision.round, votes }
			: null,
	};
}

// ─── Save Meet ───────────────────────────────────────────────────────────────

export type TsrBoxingWarning =
	/** Not every judge's card is in for every round reached: those rounds are not saved. */
	| { code: "CARDS_INCOMPLETE" }
	/** No decision yet: outcome and rank are sent as null. */
	| { code: "UNDECIDED" }
	/** The decision names a winner the cards do not. */
	| { code: "DECISION_AGAINST_CARDS" };

export interface TsrBoxingSaveInput {
	state: TsrBoxingState;
	calls: TsrBoxingCallsState | null;
	/** `[red, blue]`. */
	slots: readonly [TsrSaveSlot, TsrSaveSlot];
}

export interface TsrBoxingSaveResult {
	body: SaveResultsBody;
	warnings: TsrBoxingWarning[];
	/** "4–1 WP" / "RSC R2" / "after round 2". For the save bar. */
	summary: string;
}

/** A BOUT row of a slot: its current detail kept, the fields this mapper owns replaced. */
function detailFor(slot: TsrSaveSlot, corner: TsrBoxingCorner, input: TsrBoxingSaveInput, reached: number): BoutResultDetail {
	const { state, calls } = input;
	const i = corner === "R" ? 0 : 1;
	const log = calls?.calls.filter((c) => c.corner === corner) ?? [];
	const warnings = log.filter((c) => c.kind === "warning");
	const counts = log.filter((c) => c.kind === "count").length;
	const current = (slot.detail ?? {}) as BoutResultDetail;
	const keptTallies = (current.tallies ?? []).filter((t) => t.code !== "standing_counts" && t.code !== "cautions");
	const cautions = log.filter((c) => c.kind === "caution").length;
	const next: BoutResultDetail = {
		...current,
		rounds: Array.from({ length: reached }, (_, r) => {
			const penalties = warnings.filter((w) => w.round === r + 1).length;
			return { seq: r + 1, ...(penalties > 0 ? { penalties } : {}) };
		}),
		judges: state.cards.map((row, j) => {
			const scores = row.slice(0, reached).map((c) => c![i]!);
			return { seat: seat(j), scores, total: scores.reduce((a, b) => a + b, 0) };
		}),
		tallies: [...keptTallies, ...(counts > 0 ? [{ code: "standing_counts", value: counts }] : []), ...(cautions > 0 ? [{ code: "cautions", value: cautions }] : [])],
	};
	if (warnings.length > 0) next.penalties = warnings.length;
	else delete next.penalties;
	if (state.decision?.winner === corner) next.win_by = state.decision.method;
	else delete next.win_by;
	return next;
}

export function tsrBoxingSaveBody(input: TsrBoxingSaveInput): TsrBoxingSaveResult {
	const { state, slots } = input;
	if (!slots?.[0] || !slots?.[1]) throw new Error("Both slots (red, blue) are required");
	const warnings: TsrBoxingWarning[] = [];
	const reached = tsrBoxingRoundsScored(state);
	const inProgress = state.cards.some((row) => row.some((c, r) => c && r >= reached));
	if (inProgress) warnings.push({ code: "CARDS_INCOMPLETE" });

	const details: [BoutResultDetail, BoutResultDetail] = [detailFor(slots[0], "R", input, reached), detailFor(slots[1], "B", input, reached)];
	const verdict = boutWinner([{ detail: details[0], irm: null }, { detail: details[1], irm: null }], { decideBy: "JUDGES" });
	const winner = state.decision?.winner ?? null;
	const outcomes: [MeetOutcome, MeetOutcome] | null = winner === "R" ? ["WIN", "LOSS"] : winner === "B" ? ["LOSS", "WIN"] : null;
	if (!outcomes) warnings.push({ code: "UNDECIDED" });
	const cardsWinner = tsrBoxingCardsWinner(state);
	if (state.decision?.method === "WP" && cardsWinner && cardsWinner !== winner) warnings.push({ code: "DECISION_AGAINST_CARDS" });

	const [vr, vb] = verdict.score;
	const line = (me: 0 | 1): string | null => (reached > 0 ? `${me === 0 ? vr : vb}–${me === 0 ? vb : vr}` : null);
	const results = ([0, 1] as const).map((i) => ({
		meet_competitor_id: slots[i].meet_competitor_id,
		version: slots[i].version,
		outcome: outcomes ? outcomes[i] : null,
		rank: outcomes ? (outcomes[i] === "WIN" ? 1 : 2) : null,
		result_display: line(i),
		detail: details[i] as Record<string, unknown>,
	}));
	const decision = state.decision;
	const summary = decision ? (decision.method === "WP" ? `${Math.max(vr, vb)}–${Math.min(vr, vb)} WP` : `${decision.method}${decision.round ? ` R${decision.round}` : ""}`) : `after round ${reached}`;
	return { body: { results }, warnings, summary };
}
